import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import AddSong from './AddSong';
import AddIcon from '@mui/icons-material/Add';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { handleSongList } from '../../../../_services/creater/creatorSongList.service';
import { useDispatch, useSelector } from 'react-redux';
import { updateSongSequence } from '../../../../_services/creater/creatorSongSequence';
import SnackbarContainer from '../../../Snackbar';
import {
  setSnackBar,
  setSnackBarMessage,
  setSnackBarVariant,
} from '../../../../redux';
import { formSnackbar } from '../../../../_services/_constants/snackbar.constant';
import { handleRelatedPlatform } from '../../../../_services/creater/relatedPlatfom.service';
import { getStreamingPlatforms } from '../../../../_services/creater/streamingPlatform.service';
import { SkeletonWaveCard } from '../../../../_helpers/reusablefunctions/SkeletonCard';
import { getAllPlaylistOfUser } from '../../../../_services/playlist/getAllPlaylistOfUser';
import { Box, Dialog, IconButton, TablePagination, Tooltip, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LinkRoundedIcon from '@mui/icons-material/Link';
import logoGntunes from '../../../../img/logo4.svg';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { palette, radius, shadow, typography, motion } from '../../../../_helpers/reusableComponents/design/designTokens';
import { SHARE_SURFACE, getShareLinkStatus, hasShareLink } from '../../../../_services/creater/shareLink.service';

// The quiet actions that sit inside the public-link bar. Deliberately NOT
// `gn-actionbtn`: that class is the page's one filled red call to action, and
// three of them in a row is why the header read as a wall of buttons. These
// match the short-URL page's ghost buttons so the two screens look like one
// product.
const linkBarBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  border: `1px solid ${palette.line}`,
  borderRadius: `${radius.sm}px`,
  bgcolor: palette.surface,
  color: palette.inkSoft,
  fontSize: 12.5,
  fontWeight: 700,
  padding: '7px 12px',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: `background-color ${motion.base}, color ${motion.base}, border-color ${motion.base}`,
  '&:hover': { bgcolor: palette.surfaceAlt, color: palette.ink, borderColor: palette.hint },
};

// The same fallback chain the card uses, so the thumbnail in the phone is the
// one the creator is looking at in the list.
const posterOf = (row) => row.songPoster100x100
  || row.songPoster_url
  || (row.songPoster ? `${process.env.REACT_APP_BASEURL_MEDIA}/media/${row.songPoster}` : '')
  || (row.cover_image ? `${process.env.REACT_APP_SONGAPIURL3_MEDIA}${row.cover_image}` : '');

/**
 * The creator's public music page, in a phone, beside the list that writes it.
 *
 * Drawn rather than framed: gntunes.com answers `X-Frame-Options: SAMEORIGIN`,
 * so an iframe of it renders nothing at all — and an iframe would only catch up
 * on a reload, where this changes as the row does. The shape is the microsite's
 * own (`gn-sparkle`): the banner hero with the page title over it, the
 * breadcrumb, the song cards, the pager, and the footer that invites the
 * visitor to make a page of their own.
 *
 * It shows only the songs whose toggle is ON, because that is what a visitor
 * gets — a song vanishing from the phone as it is switched off is the clearest
 * answer to what that toggle does.
 */
const PhoneShell = ({ banner, title, crumbs, children }) => (
  <div className="phone">
    <div className="phone__frame">
      <div className="phone__notch" />
      <div className="phone__screen">
        <div
          className="phone__hero"
          style={banner ? { backgroundImage: `url("${banner}")` } : undefined}
        >
          <span className="phone__burger">
            <span /><span /><span />
          </span>
          <div className="phone__heroTitle">{title}</div>
          <div className="phone__crumbs">
            {crumbs.map((crumb, index) => (
              <React.Fragment key={crumb}>
                {index > 0 && <span className="phone__crumbSep">◆</span>}
                <span>{crumb}</span>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="phone__body">{children}</div>

        <div className="phone__footer">
          <img src={logoGntunes} alt="GNTunes" className="phone__footerLogo" />
          <div className="phone__footerCta">Create Your Profile</div>
          <div className="phone__footerLegal">
            By using this service you agree to our <b>Privacy Policy</b> and <b>Terms</b> Of Use.
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PhoneMusicPage = ({ songs, banner, loading }) => {
  const visible = songs.filter((row) => row.visible);
  return (
    <PhoneShell banner={banner} title="Music" crumbs={['My Hub', 'Music']}>
      {loading && [1, 2, 3].map((i) => (
        <div className="phone__song" key={i}>
          <span className="phone__songArt" />
          <span className="phone__songSkeleton" />
        </div>
      ))}

      {!loading && visible.length === 0 && (
        <div className="phone__empty">
          No song is switched on yet, so your music page is empty.
        </div>
      )}

      {!loading && visible.map((row) => (
        <div className="phone__song" key={row.id}>
          {posterOf(row)
            ? <img className="phone__songArt" src={posterOf(row)} alt="" />
            : <span className="phone__songArt" />}
          <span className="phone__songBody">
            <span className="phone__songTitle">{row.songTitle || 'Untitled song'}</span>
            {row.artists && <span className="phone__songArtist">{row.artists}</span>}
          </span>
        </div>
      ))}

      {/* The microsite pages ten at a time and draws the pager even on a single
          page, so the preview does too — it is part of the shape of the page. */}
      {!loading && visible.length > 0 && (
        <div className="phone__pager">
          <span className="phone__pagerBtn is-off">Prev</span>
          <span className="phone__pagerLabel">
            Page 1 of {Math.max(1, Math.ceil(visible.length / 10))}
          </span>
          <span className={`phone__pagerBtn ${visible.length > 10 ? '' : 'is-off'}`}>Next</span>
        </div>
      )}
    </PhoneShell>
  );
};

function SongListComp() {
  const userData = useSelector((state) => state.userData.userData);
  const dispatch = useDispatch();
  const history = useHistory();

  const [songsList, setSongsList] = useState([]);
  const [songListBKP, setSongListBKP] = useState([]);
  const [disableStatus, setDisableStatus] = useState(false);
  const [allPlatfromList, setAllPlatformList] = useState([]);
  const [allPlatfromListCopy, setAllPlatformListCopy] = useState([]);
  const [relatedPlatform, setRelatedPlatform] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStart, setLoadingStart] = useState(true);
  const [playlistItems, setPlaylistItems] = useState([]);
  const [playlistLoading, setPlaylistLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState()
  // Whether a gn2.me short URL already exists for this creator's music hub —
  // which is what the button below is LABELLED from. Read with the non-minting
  // probe: asking the ordinary way would answer "is there one?" by creating
  // one, and every visit to Song Hub would mint a link nobody asked for.
  // `null` means we have not heard back yet, and the button stays neutral
  // rather than guessing wrong and re-labelling itself under the cursor.
  const [musicHubLink, setMusicHubLink] = useState(null);
  // Below 1200px the rail beside the list has nowhere to stand, so the phone
  // moves behind a button — the same one the song's own screen uses.
  const [previewOpen, setPreviewOpen] = useState(false);

  const breadcrumbs = [
    { label: 'Song Hub', link: '/songhub' },
    { label: 'Music', link: '/songhub' },
  ];

  const musicHubUrl = `${process.env.REACT_APP_CREATORURL}${userData?.userHandle}/music`;

  // One probe per visit, and it never blocks the song list: the button renders
  // in its neutral state immediately and settles into "Create" or "View" when
  // this answers. A probe that fails leaves it neutral, which is the honest
  // reading of "we do not know" and still opens the page when pressed.
  useEffect(() => {
    let cancelled = false;
    getShareLinkStatus(SHARE_SURFACE.MUSIC_HUB).then((response) => {
      if (cancelled) return;
      setMusicHubLink(hasShareLink(response) ? response.data : false);
    });
    return () => { cancelled = true; };
  }, []);

  const showError = (msg = formSnackbar.errors.errorMsg) => {
    dispatch(setSnackBar(true));
    dispatch(setSnackBarMessage(msg));
    dispatch(setSnackBarVariant('error'));
    setTimeout(() => dispatch(setSnackBar(false)), 2000);
  };

  const getSongList = async () => {
    try {
      setLoadingStart(true);
      const dataObj = { userId: userData.id };
      const limit = rowsPerPage;
      const offset = page;
      const response = await handleSongList(dataObj, 'GETBYUSER', limit, offset);
      if (response.status === 200 || response.status === 204 || response.status === 201) {
        const results = response.data.results || [];
        setSongListBKP(results);
        setTotalCount(response?.data?.total_count)

        if (results.length > 0) {
          const data = [...results].sort((a, b) => parseInt(a.song_order) - parseInt(b.song_order));
          setSongsList(data);
          setDisableStatus(false);
        } else {
          setDisableStatus(true);
          setSongsList([{}, ...songsList]);
        }
      } else {
        showError();
      }
    } catch {
      showError();
    }
    finally {
      setLoadingStart(false);
    }
  };

  useEffect(() => {
    if (userData.id) getSongList();
  }, [userData.id, page, rowsPerPage,]);

  const addNewSongHandler = () => {
    setSongsList([{}, ...songsList]);
    setDisableStatus(true);
  };

  const deleteSongHandler = async (id) => {
    const items = songsList;
    setSongsList(items.filter((item) => item.id !== id));
    setDisableStatus(false);
    if (id !== undefined) {
      dispatch(setSnackBar(true));
      dispatch(setSnackBarMessage(formSnackbar.songPlaylist.songLinkDelete));
      dispatch(setSnackBarVariant('info'));
      if (JSON.stringify(items[0]) === '{}') setDisableStatus(true);
      try {
        const dataObj = { userId: userData.id, songId: id };
        const response = await handleSongList(dataObj, 'DELETE');
        if (response.status === 200 || response.status === 204) {
          setTimeout(() => dispatch(setSnackBar(false)), 2000);
          dispatch(setSnackBarMessage(formSnackbar.songPlaylist.songLinkDeleteSuccess));
          dispatch(setSnackBarVariant('success'));
        } else {
          showError();
        }
      } catch {
        showError();
      }
    }
  };

  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(songsList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setSongsList(items);
    const data = items.map(song => song.id);
    try {
      const dataObj = { song_ids: data.join(',') };
      const response = await updateSongSequence(userData.id, dataObj);
      if (response.status === 200) {
        getSongList();
      } else {
        showError();
      }
    } catch {
      showError();
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      getPlatformList();
    };
    fetchInitialData();
    // eslint-disable-next-line
  }, []);

  const getRelatedPlatforms = async (id) => {
    if (id) {
      try {
        setLoading(true);
        const dataObj = { songId: id, userId: userData.id };
        const response = await handleRelatedPlatform(dataObj, 'GETBYSONG');
        if (response.status === 200) {
          const arrayData = response.data.data || [];
          const data = arrayData.sort((a, b) => parseInt(a.platform_order) - parseInt(b.platform_order));
          setRelatedPlatform(data);
        } else {
          showError();
        }
      } catch {
        showError();
      }
      finally {
        setLoading(false);
      }
    }
  };

  const reRenderPlatformHandler = (id) => getRelatedPlatforms(id);

  const getPlatformList = async () => {
    try {
      const platformRes = await getStreamingPlatforms();
      if (platformRes.status === 200) {
        setAllPlatformList(platformRes.data.data);
        setAllPlatformListCopy(platformRes.data.data);
      } else {
        showError();
      }
    } catch {
      showError();
    }
  };

  // -------createplaylist function
  const getPlaylistList = useCallback(() => {
    setPlaylistLoading(true);
    getAllPlaylistOfUser({ search: userData.id, limit: 5, offset: 0 })
      .then((response) => {
        setPlaylistItems(response.data.results);
        setPlaylistLoading(false);
      })
      .catch((error) => {
        setPlaylistLoading(false);
        // handle error if needed
      });
  }, [userData.id]);

  // -----------pagination functions

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    const newPage = Math.floor((page * rowsPerPage) / newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(newPage);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };


  const renderCard = useCallback(
    (card, index, provided) => (
      <AddSong
        key={card.id || index}
        deleteSongHandler={deleteSongHandler}
        index={index}
        id={card.id}
        data={card}
        songTitle={card.songTitle}
        provided={provided}
        disableHandler={() => setDisableStatus(false)}
        renderSongList={getSongList}
        songsListData={songsList}
        allPlatfromList={allPlatfromList}
        allPlatfromListCopy={allPlatfromListCopy}
        setAllPlatformList={setAllPlatformList}
        relatedPlatform={relatedPlatform}
        setRelatedPlatform={setRelatedPlatform}
        reRenderPlatformHandler={reRenderPlatformHandler}
        getRelatedPlatforms={getRelatedPlatforms}
        loading={loading}
        // playlist state and functions
        setPlaylistLoading={setPlaylistLoading}
        playlistLoading={playlistLoading}
        setPlaylistItems={setPlaylistItems}
        playlistItems={playlistItems}
        getPlaylistList={getPlaylistList}
      />
    ),
    [
      songsList,
      allPlatfromList,
      allPlatfromListCopy,
      relatedPlatform,
      loading,
      playlistLoading
    ]
  );

  // Built once and rendered twice — in the rail on a desktop, in the dialog on
  // anything narrower. A second, smaller preview would be a second thing to
  // keep true.
  const phone = (
    <PhoneMusicPage
      songs={songsList}
      banner={userData && userData.banner_img
        ? `${process.env.REACT_APP_BASEURL_MEDIA}/media/${userData.banner_img}`
        : ''}
      loading={loadingStart}
    />
  );

  return (
    <div className="container-fluid py-4 creator-playlist-panel">
      <div className="row m-0">
        {/* List on the left, the page it publishes on the right. Below 1200px
            the phone steps aside rather than squeezing the songs. */}
        <div className="col-12 col-xl-8">
          {/* Page header. ONE filled red button on this screen — "Add new" —
              sitting opposite the title where a page-level action belongs. The
              public address of the hub used to be a raw browser-blue link with
              a second red button beside it, which read as two equal calls to
              action and neither of them was the one people came here for. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h2" sx={{ ...typography.pageTitle, mb: 0 }}>
                Song Hub
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                {breadcrumbs.map((breadcrumb, index) => (
                  <React.Fragment key={breadcrumb.label}>
                    {index > 0 && (
                      <Box component="span" sx={{ px: 1, color: palette.hint, fontSize: 10 }}>
                        ◆
                      </Box>
                    )}
                    <Link
                      to={breadcrumb.link}
                      style={{ textDecoration: 'none', color: palette.muted, fontSize: '0.8rem' }}
                    >
                      {breadcrumb.label}
                    </Link>
                  </React.Fragment>
                ))}
              </Box>
            </Box>

            <button
              disabled={disableStatus}
              className="gn-actionbtn d-inline-flex align-items-center"
              style={{ gap: '6px', padding: '9px 18px', flexShrink: 0 }}
              onClick={addNewSongHandler}
            >
              <AddIcon fontSize="small" />
              ADD NEW
            </button>
          </Box>

          {/* The hub's public address, and its short form, in one row.
              The short-URL action belongs HERE rather than out on its own: it is
              a second reading of this same link, not a second thing to do.

              It wears the SONG LIST's column classes so the two line up edge to
              edge: full width above a half-width list left the address stranded
              at one end of an empty strip.

              No card behind it: a surface, a border and an icon tile around one
              line of text read as a THING on the page, competing with the song
              cards below. The address sits straight on the page instead — the
              button keeps its own outline, which is enough to find it. */}
          <div className="row">
          <div className="col-sm-12 col-md-12 col-lg-8 col-xl-12">
          <Box
            sx={{
              mt: 3,
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            {/* `flex: 1 1 240px` with `minWidth: 0` is what lets a long handle
                truncate instead of shoving the button off the row. The address
                itself is the link — clicking it opens the page, and it is
                selectable for anyone who wants to copy it by hand. */}
            <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
              <Typography
                component="a"
                href={musicHubUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={musicHubUrl}
                sx={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 500,
                  color: palette.inkSoft,
                  textDecoration: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  transition: `color ${motion.base}`,
                  '&:hover': { color: palette.brand, textDecoration: 'underline' },
                }}
              >
                {musicHubUrl}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {/* One button, two readings. Before the probe answers it says
                  "Short URL" — neutral, because relabelling itself under the
                  cursor a second after the page settles is worse than being
                  briefly vague. */}
              <Tooltip
                arrow
                title={musicHubLink === null
                  ? 'Short URL for your music hub'
                  : musicHubLink
                    ? `${musicHubLink.url} — open its page for the QR code and click analytics`
                    : 'Create a short, shareable gn2.me address for your music hub'}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => history.push('/songhub/short-url')}
                  sx={{
                    ...linkBarBtn,
                    color: palette.brand,
                    '&:hover': {
                      bgcolor: palette.brandSofter,
                      color: palette.brandDark,
                      borderColor: palette.brand,
                    },
                  }}
                >
                  {musicHubLink
                    ? <InsightsOutlinedIcon sx={{ fontSize: 16 }} />
                    : <LinkRoundedIcon sx={{ fontSize: 16 }} />}
                  {musicHubLink === null
                    ? 'Short URL'
                    : (musicHubLink ? 'View short URL' : 'Create short URL')}
                </Box>
              </Tooltip>
            </Box>
          </Box>
          </div>
          </div>
          {
            loadingStart ? (
              <div>
                {[1, 2, 3].map((i) => (
                  <SkeletonWaveCard key={i} />
                ))}
              </div>
            ) : (
              <div className="row pb-5">
                <DragDropContext onDragEnd={handleOnDragEnd}>
                  <Droppable droppableId="characters">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        // Back to the original half-panel width. It was
                        // widened to col-12 only because the details panel used
                        // to expand INSIDE the card and needed the room; that
                        // panel is a page of its own now, so the card is a row
                        // of a list again and reads better narrow.
                        className="characters creator-songs-list col-sm-12 col-md-12 col-lg-8 col-xl-12"
                      >
                        {songsList.length > 0 &&
                          songsList.map((item, index) => (
                            <Draggable
                              key={item.id || index}
                              draggableId={String(item.id || index)}
                              index={index}
                            >
                              {(provided) => renderCard(item, index, provided)}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                <TablePagination
                  rowsPerPageOptions={[10, 20, 30]}
                  colSpan={3}
                  sx={{ width: '100%', marginTop: '20px', padding: '32px 0px 32px 32px' }}
                  className='cardPagination'
                  component='div'
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            )
          }

        </div>

        <div className="col-xl-4 d-none d-xl-block">
          <div className="phone-rail">{phone}</div>
        </div>
      </div>
      {/* The way back to the preview on a screen too narrow to keep it open.
          A pill rather than a round FAB: the word is the point, and it sits
          above the fixed copyright bar rather than under it. Hidden from
          1200px up — MUI's `lg`, which is where Bootstrap's `d-xl-block` puts
          the rail on screen — because a button offering to show a phone that
          is already showing is offering nothing. */}
      <Box
        component="button"
        type="button"
        onClick={() => setPreviewOpen(true)}
        sx={{
          display: { xs: 'inline-flex', lg: 'none' },
          alignItems: 'center',
          gap: 0.75,
          position: 'fixed',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: 64,
          zIndex: 1150,
          px: 2,
          py: 1,
          border: `1px solid ${palette.line}`,
          borderRadius: '999px',
          bgcolor: palette.surface,
          color: palette.ink,
          fontSize: 13,
          fontWeight: 700,
          boxShadow: '0 8px 24px rgba(15, 23, 41, 0.16)',
          cursor: 'pointer',
          transition: `color ${motion.base}, border-color ${motion.base}`,
          '&:hover': { color: palette.brand, borderColor: palette.brand },
        }}
      >
        <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
        Preview
      </Box>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth={false}
        PaperProps={{
          // The phone brings its own frame and shadow, so the dialog around it
          // is nothing but a way to close it.
          sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible', m: 2 },
        }}
      >
        <IconButton
          onClick={() => setPreviewOpen(false)}
          aria-label="Close preview"
          size="small"
          sx={{
            alignSelf: 'flex-end',
            mb: 1,
            bgcolor: palette.surface,
            color: palette.inkSoft,
            boxShadow: shadow.card,
            '&:hover': { bgcolor: palette.brandSoft, color: palette.brand },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        {phone}
      </Dialog>

      <SnackbarContainer />
    </div>
  );
}

export default SongListComp;
